// © Microsoft Corporation. All rights reserved.
// Provides utility functions to get and set cookies

import { ConnectorNamespaces, CookieConsentProvider, CookieConsentStatus, SameSiteValues } from "@msnews/experiences-constants";
import { CookieExpirationTooLong, CookieNotCategorized, CookieSizeTooBig, InvalidCookie, SharedStateConnectorNull } from "@msnews/diagnostics";
import { OneTrustCookieCategory, OneTrustCookieCategoryMapping } from "@msnews/cookie-consent-common";

import { ExperiencesSharedStateConnector } from "@msnews/experiences-shared-state";
import { PageBase } from "@msnews/experiences-page-base";
import { Telemetry } from "@msnews/telemetry-contracts";
import { setRequireFunctionalCookie } from "@msnews/core";

// Functional cookies can be written irrespective of the cookie consent status.
// NOTE: These should always be in lower case (preferably in alpha-numeric order)
const functionalCookies = [
    "aace",
    "app_anon",
    "at",
    "cac",
    "cbypass",
    "child",
    "ddlcur",
    "hidebn",
    "ipt",
    "mktreset",
    "nactk",
    "ntps",
    "preferencesmsn",
    "smd",
    "sptmarket"
];

// Known list of "big" cookies for which we don't want to log an error.
const knownBigCookies = [
    "at",
    "at-bing",
    "eupubconsent-v2",
    "lt",
    "MSNRPSSecAuth"
];

// As per the spec for Cookies: https://curl.se/rfc/cookie_spec.html:
// NAME=VALUE - "This string is a sequence of characters excluding semi-colon, comma and white space."
const invalidCookieChars = [
    ";",
    ",",
    " "
];

/**
 * Helper function used to set a cookie
 * When expiryDays is equal to -1 it will unset the cookie
 * * @param {string} name Cookie name
 * * @param {string} value Cookie value
 * * @param {number} expiryDays Cookie expiry days
 * * @param {string} domain Cookie domain
 * * @param {string} path Cookie path
 * * @param {boolean} secure Is it a https only cookie
 * * @param {OneTrustCookieCategory} cookieCategory The OneTrust category the given cookie belongs to
 * * @param {sameSiteValue} sameSiteValue The optional value to use for samesite cookie property
 * * @returns {boolean} indicate if cookie saved successfully
 */
export function setCookie(name: string, value: string, expiryDays?: number, domain?: string, path?: string, secure?: boolean, cookieCategory?: OneTrustCookieCategory, sameSiteValue?: SameSiteValues): boolean {
    if (expiryDays > 0) {
        if (!isSetCookiePermitted(name, cookieCategory)) {
            return false;
        }

        if (value === undefined || value === null) {
            Telemetry.sendAppErrorEvent({
                ...InvalidCookie,
                message: `Cookie value is being set to either 'undefined' or 'null'. cookie name: "${name}", cookie value: "${value}"`
            });

            return false;
        }

        for (const invalidChar of invalidCookieChars) {
            if ((name.indexOf(invalidChar) > -1) || (value.indexOf(invalidChar) > -1)) {
                Telemetry.sendAppErrorEvent({
                    ...InvalidCookie,
                    // Can't log cookie value since it might contain PII
                    message: `Cookie name and/or value contains invalid character: '${invalidChar}'. cookie name: "${name}"`
                });

                return false;
            }
        }

        if (expiryDays > 365) {
            Telemetry.sendAppErrorEvent({
                ...CookieExpirationTooLong,
                // Can't log cookie value since it might contain PII
                message: `Cookie expiration time: ${expiryDays} days is greater than suggested max duration of 365 days (1 year). cookie name: "${name}"`
            });
        }

        const cookieSize =  name.length + (value && value.length || 0);
        if (cookieSize> 1000 && !knownBigCookies.includes(name)) {
            Telemetry.sendAppErrorEvent({
                ...CookieSizeTooBig,
                // Can't log cookie value since it might contain PII
                message: `Cookie size: "${cookieSize} Bytes" is too big (> 1000 Bytes). cookie name: "${name}"`
            });
        }
    }

    setRequireFunctionalCookie(name, value, expiryDays, domain, path, secure, sameSiteValue);
    return true;
}

/**
 * Helper function used to check if cookie setting is permitted
 * * @param {string} cookieName Cookie name
 * * @param {OneTrustCookieCategory} cookieCategoryOverride The OneTrust category override provided by the setCookie caller
 * * @returns {boolean} True is cookie setting permitter, false otherwise
 */
function isSetCookiePermitted(cookieName: string, cookieCategoryOverride?: OneTrustCookieCategory): boolean {
    // If cookie consent status is "NotRequired", cookies can be written.
    const sharedStateConnector: ExperiencesSharedStateConnector = PageBase.getInstance().rootReducer.connector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;

    if (!sharedStateConnector) {
        Telemetry.sendAppErrorEvent(SharedStateConnectorNull);
        return false;
    }

    const currentSharedState = sharedStateConnector.getCurrentState();
    const consentStatus: CookieConsentStatus = currentSharedState.cookieConsentStatus;
    const consentProvider: CookieConsentProvider = currentSharedState.cookieConsentProvider;

    let isSetAllowed = false;
    switch (consentProvider) {
        case CookieConsentProvider.OneTrustBanner:
        case CookieConsentProvider.WidgetHost: {

            isSetAllowed = isCookieInOneTrustActiveCategory(cookieName, currentSharedState.activeCookieCategories, cookieCategoryOverride);
            break;
        }
        case CookieConsentProvider.MSCCBanner: {

            isSetAllowed = isCookieInFunctionalCategory(cookieName, consentStatus);
            break;
        }
        default:
            isSetAllowed = consentStatus === CookieConsentStatus.NotRequired;
            break;
    }

    // if we reach here, we do not allow cookie to be set by returning false
    return isSetAllowed;
}

/**
 * Check (case-sensitive check) if cookie is in OneTrust active categories
 *
 * @param cookieName Cookie name
 * @param activeCategories The active categories returned by OneTrust API
 * @param cookieCategoryOverride The OneTrust category override provided by the setCookie caller
 */
function isCookieInOneTrustActiveCategory(cookieName: string, activeCategories: Array<OneTrustCookieCategory>, cookieCategoryOverride?: OneTrustCookieCategory): boolean {

    if (cookieCategoryOverride) {

        // check if the provided cookie category override is in the active categories returned by OneTrust
        if (activeCategories.includes(cookieCategoryOverride)) {
            return true;
        }

    } else {
        // If no override is provided, check the hardcoded cookie mapping in the active cookie categories
        for (const activeCategory of activeCategories) {
            const categoryCookies = OneTrustCookieCategoryMapping[activeCategory];

            if (categoryCookies.includes(cookieName)) {
                return true;
            }
        }
    }

    // Check if the cookieName is present in the remaining cookie categories and fire an app error if not present
    let cookieCategorized = false;
    const remainingCategories = Object.keys(OneTrustCookieCategoryMapping).filter(category => !activeCategories.includes(OneTrustCookieCategory[category]));

    // iterate through remaining category values
    for (const remainingCategory of remainingCategories) {
        const categoryCookies = OneTrustCookieCategoryMapping[remainingCategory];

        if (categoryCookies.includes(cookieName)) {
            cookieCategorized = true;
            break;
        }
    }

    if (!cookieCategorized) {
        Telemetry.sendAppErrorEvent({
            ...CookieNotCategorized,
            message: `Attempting to write a cookie: "${cookieName}" not present in the cookie category mapping.`
        });
    }

    return false;
}

/**
 * Check (case-insensitive check) if cookie is in Functional category
 * @param cookieName Cookie name
 * @param consentStatus Cookie consent status
 */
function isCookieInFunctionalCategory(cookieName: string, consentStatus: CookieConsentStatus) {
    if (consentStatus === CookieConsentStatus.NotRequired) {
        return true;
    }

    const lowerCaseCookieName = cookieName.toLowerCase();
    if (functionalCookies.indexOf(lowerCaseCookieName) >= 0) {
        return true;
    }

    return false;
}